/**
 * PromotoresLeilao.jsx
 * Componente para gerenciar promotores vinculados a um leilão.
 * Usado na criação/edição do leilão (aba Financeiro).
 */
import { useState, useEffect } from 'react'
import { Plus, X, Edit2, Users } from 'lucide-react'
import toast from 'react-hot-toast'
import promotoresService from '../../services/promotoresService'

export default function PromotoresLeilao({ organizadorId, leilaoId, promotoresHabilitado, titulo = 'Promotores do Leilão', labelItem = 'Promotor' }) {
  const [promotores, setPromotores] = useState([]) // cadastro geral
  const [vinculados, setVinculados] = useState([]) // vinculados ao leilão
  const [loading, setLoading] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [selectedId, setSelectedId] = useState('')
  const [percVenda, setPercVenda] = useState('')
  const [percDefesa, setPercDefesa] = useState('')
  const [criandoNovo, setCriandoNovo] = useState(false)
  const [novoNome, setNovoNome] = useState('')

  useEffect(() => {
    if (!organizadorId || !promotoresHabilitado) return
    carregarPromotores()
    if (leilaoId) carregarVinculados()
  }, [organizadorId, leilaoId, promotoresHabilitado])

  const carregarPromotores = async () => {
    try {
      const data = await promotoresService.listar(organizadorId, { ativo: true })
      setPromotores(data)
    } catch { }
  }

  const carregarVinculados = async () => {
    if (!leilaoId) return
    setLoading(true)
    try {
      const data = await promotoresService.listarPorLeilao(organizadorId, leilaoId)
      setVinculados(data)
    } catch { }
    finally { setLoading(false) }
  }

  const fecharAdd = () => {
    setShowAdd(false)
    setCriandoNovo(false)
    setSelectedId('')
    setNovoNome('')
    setPercVenda('')
    setPercDefesa('')
  }

  const handleAdd = async () => {
    try {
      let promotorId = selectedId
      if (criandoNovo || disponiveis.length === 0) {
        if (!novoNome.trim()) { toast.error(`Informe o nome do ${labelItem.toLowerCase()}`); return }
        const novo = await promotoresService.criar(organizadorId, {
          nome: novoNome.trim(),
          perc_venda: parseFloat(percVenda) || 0,
          perc_defesa: parseFloat(percDefesa) || 0,
        })
        promotorId = novo.id
        setPromotores(prev => [...prev, novo])
      } else if (!selectedId) {
        toast.error(`Selecione um ${labelItem.toLowerCase()}`)
        return
      }
      await promotoresService.vincular(organizadorId, leilaoId, {
        promotor_id: parseInt(promotorId),
        perc_venda: parseFloat(percVenda) || 0,
        perc_defesa: parseFloat(percDefesa) || 0,
      })
      toast.success(`${labelItem} vinculado`)
      fecharAdd()
      carregarVinculados()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao vincular')
    }
  }

  const removerVinculo = async (vinculoId) => {
    try {
      await promotoresService.desvincular(organizadorId, leilaoId, vinculoId)
      toast.success(`${labelItem} removido`)
      carregarVinculados()
    } catch { toast.error('Erro ao remover') }
  }

  const handleRemove = (vinculoId) => {
    toast((t) => (
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-gray-800">Remover {labelItem.toLowerCase()} deste leilão?</p>
        <div className="flex gap-2 mt-1">
          <button onClick={() => { toast.dismiss(t.id); removerVinculo(vinculoId) }}
            className="flex-1 px-3 py-1.5 text-xs font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700">
            Remover
          </button>
          <button onClick={() => toast.dismiss(t.id)}
            className="flex-1 px-3 py-1.5 text-xs font-semibold border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50">
            Cancelar
          </button>
        </div>
      </div>
    ), { duration: 10000, position: 'top-center', style: { maxWidth: '340px' } })
  }

  const handleSelectPromotor = (id) => {
    setSelectedId(id)
    const p = promotores.find(pr => String(pr.id) === id)
    if (p) {
      setPercVenda(String(p.perc_venda || ''))
      setPercDefesa(String(p.perc_defesa || ''))
    }
  }

  if (!promotoresHabilitado) return null
  if (!leilaoId) return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-sm text-indigo-700">
      <Users className="w-4 h-4 inline mr-1.5" />
      Salve o leilão primeiro para vincular {labelItem.toLowerCase()}es.
    </div>
  )

  // Promotores disponíveis (não vinculados)
  const disponiveis = promotores.filter(p => !vinculados.some(v => v.promotor_id === p.id))

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
          <Users className="w-4 h-4 text-indigo-600" />
          {titulo}
        </h4>
        {!showAdd && (
          <button onClick={() => { setShowAdd(true); setCriandoNovo(disponiveis.length === 0) }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-lg transition-colors">
            <Plus className="w-3.5 h-3.5" /> {disponiveis.length === 0 ? `Novo ${labelItem}` : 'Adicionar'}
          </button>
        )}
      </div>

      {/* Lista de vinculados */}
      {loading ? (
        <div className="text-center py-4 text-gray-400 text-sm">Carregando...</div>
      ) : vinculados.length === 0 ? (
        <div className="text-center py-4 text-gray-400 text-sm border border-dashed border-gray-200 rounded-lg">
          Nenhum {labelItem.toLowerCase()} vinculado a este leilão.
        </div>
      ) : (
        <div className="space-y-2">
          {vinculados.map(v => (
            <div key={v.id} className="flex items-center justify-between p-3 bg-indigo-50 border border-indigo-100 rounded-lg">
              <div>
                <span className="font-medium text-gray-800 text-sm">{v.nome}</span>
                {v.telefone && <span className="text-xs text-gray-500 ml-2">{v.telefone}</span>}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-semibold text-indigo-700">V: {v.perc_venda}%</span>
                <span className="text-xs font-semibold text-orange-600">D: {v.perc_defesa}%</span>
                <button onClick={() => handleRemove(v.id)}
                  className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form adicionar */}
      {showAdd && (
        <div className="border border-indigo-200 rounded-lg p-4 bg-white space-y-3">
          {criandoNovo || disponiveis.length === 0 ? (
            <div>
              <label className="block text-xs text-gray-500 font-medium mb-1">Nome do novo {labelItem.toLowerCase()}</label>
              <input type="text" value={novoNome} onChange={e => setNovoNome(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                placeholder={`Nome do ${labelItem.toLowerCase()}`} autoFocus />
              {disponiveis.length > 0 && (
                <button type="button" onClick={() => { setCriandoNovo(false); setNovoNome('') }}
                  className="text-xs text-indigo-600 hover:text-indigo-800 mt-1">
                  Ou selecione um já cadastrado
                </button>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-xs text-gray-500 font-medium mb-1">{labelItem}</label>
              <select value={selectedId} onChange={e => handleSelectPromotor(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500">
                <option value="">Selecione...</option>
                {disponiveis.map(p => (
                  <option key={p.id} value={String(p.id)}>{p.nome} (V:{p.perc_venda}% D:{p.perc_defesa}%)</option>
                ))}
              </select>
              <button type="button" onClick={() => setCriandoNovo(true)}
                className="text-xs text-indigo-600 hover:text-indigo-800 mt-1">
                + Criar novo {labelItem.toLowerCase()}
              </button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 font-medium mb-1">% Venda</label>
              <input type="number" step="0.01" min="0" max="100" value={percVenda}
                onChange={e => setPercVenda(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                placeholder="0.00" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 font-medium mb-1">% Defesa</label>
              <input type="number" step="0.01" min="0" max="100" value={percDefesa}
                onChange={e => setPercDefesa(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                placeholder="0.00" />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={fecharAdd}
              className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
            <button onClick={handleAdd} disabled={criandoNovo || disponiveis.length === 0 ? !novoNome.trim() : !selectedId}
              className="px-3 py-1.5 text-xs text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50">
              Vincular
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
